import React, { lazy, Suspense, useState, useEffect } from "react";
import { AppLoading } from "@/components/app-loading";
import { Switch, Route, useLocation } from "wouter";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { queryClient as defaultQueryClient } from "./lib/queryClient";
// Lazy-load Radix-based Toaster — pulls in @radix-ui/react-toast (~10 KB)
// and only matters after the user triggers a useToast() call. Wrapped in
// ClientOnly + Suspense fallback={null}, so it never blocks paint or
// hydration.
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
// Lazy-load sonner — toast library is ~63 KB unminified and only needed
// after the user triggers a notification. Renders inside ClientOnly +
// Suspense fallback={null} so it never blocks paint or hydration.
const SonnerToaster = lazy(() =>
  import("sonner").then((m) => ({ default: m.Toaster })),
);
// Lazy-load the site-wide workshop promo popup — only mounts on non-workshop
// pages (route exclusion in SiteRouter) and only renders when triggered.
const GlobalPromoPopup = lazy(() =>
  import("@/components/ui/global-promo-popup").then((m) => ({ default: m.GlobalPromoPopup })),
);
// Reading pages (blog, case studies, tools) offer the free toolkit instead.
const ToolkitPopup = lazy(() =>
  import("@/components/ui/toolkit-popup").then((m) => ({ default: m.ToolkitPopup })),
);
import { TooltipProvider } from "@/components/ui/tooltip";
import { SmoothScrollProvider, getLenis } from "@/lib/smooth-scroll";
import { BackToTop } from "@/components/back-to-top";
import { JsonLd } from "@/components/seo/json-ld";
import { organizationSchema, websiteSchema } from "@/lib/seo";
import { PRO_LIVE } from "@shared/members-launch";

// EventDetail is imported EAGERLY (not lazy) to match the server's
// routes-ssr.tsx. When the SSR HTML for /events/:slug renders the full
// event-detail tree, the client must hydrate against the same tree
// synchronously — a lazy import here would cause React 18 to render
// the Suspense fallback (a blank <div>) until the chunk arrives, which
// blanks the page on first navigation. Refresh worked because the chunk
// was already in browser cache. Eager-importing eliminates the race.
import EventDetail from "@/pages/event-detail";
import { rememberLandingAttribution } from "@/lib/lead-attribution";
import { isAuthScreenPath, isToolkitPath } from "@/lib/auth-screens";

// ─── Existing pages ──────────────────────────────────────────────────────────
// Testimonials/Measurement/TaylorSwiftEffect were restored eagerly after the
// 10ddb88 unrouting accident; nothing about them needs the eager treatment —
// they're ordinary SSR'd pages like the ~50 lazy routes below.
const Testimonials = lazy(() => import("@/pages/testimonials"));
const Measurement = lazy(() => import("@/pages/measurement"));
const TaylorSwiftEffect = lazy(() => import("@/pages/taylor-swift-effect-impact-on-nfl-super-bowl-lviii"));
const NotFound = lazy(() => import("@/pages/not-found"));

// Membership surface
const MemberLogin = lazy(() => import("@/pages/members/login"));
const MemberSignup = lazy(() => import("@/pages/members/signup"));
const MemberClaim = lazy(() => import("@/pages/members/claim"));
const MemberAuthVerify = lazy(() => import("@/pages/members/auth-verify"));
const MemberAuthMagic = lazy(() => import("@/pages/members/auth-magic"));
const MemberAuthReset = lazy(() => import("@/pages/members/auth-reset"));
const MemberDashboard = lazy(() => import("@/pages/members/dashboard"));
const MemberSlides = lazy(() => import("@/pages/members/slides"));
const DevTutorials = lazy(() => import("@/pages/members/dev-tutorials"));
const DesignBuilder = lazy(() => import("@/pages/design-builder"));
const MembershipPage = lazy(() => import("@/pages/membership"));
const MembershipSuccess = lazy(() => import("@/pages/membership-success"));
const MembershipFirstMonth = lazy(() => import("@/pages/membership-first-month"));
const AiContextToolkit = lazy(() => import("@/pages/ai-context-toolkit"));
// Agent Context Kit, moved in from agentcontextkit.com (client/src/pages/kit/index.tsx).
const KitHome = lazy(() => import("@/pages/kit").then((m) => ({ default: m.KitHomePage })));
const KitBuild = lazy(() => import("@/pages/kit").then((m) => ({ default: m.KitBuildPage })));
const KitImprove = lazy(() => import("@/pages/kit").then((m) => ({ default: m.KitImprovePage })));
const KitAudit = lazy(() => import("@/pages/kit").then((m) => ({ default: m.KitAuditPage })));
const KitMethod = lazy(() => import("@/pages/kit").then((m) => ({ default: m.KitMethodPage })));
// AdVenture Plus sells at /membership; PRO_LIVE gates the routes off (→ 404).
const Home = lazy(() => import("@/pages/home"));
const DesignSystem = lazy(() => import("@/pages/design-system"));
const DesignSystemSymbols = lazy(() => import("@/pages/design-system-symbols"));
const NavWireframe = lazy(() => import("@/pages/nav-wireframe"));
const Blog = lazy(() => import("@/pages/blog"));
const BlogPost = lazy(() => import("@/pages/blog-post"));
const CaseStudies = lazy(() => import("@/pages/case-studies"));
const CaseStudy = lazy(() => import("@/pages/case-study"));
const AboutUs = lazy(() => import("@/pages/about-us"));
const InstructorsIndex = lazy(() => import("@/pages/instructors/instructors-index"));
const InstructorDetail = lazy(() => import("@/pages/instructors/instructor-detail"));
const VipSession = lazy(() => import("@/pages/vip-session"));
const NyfWorkshop = lazy(() => import("@/pages/nyf-workshop"));
const Contact = lazy(() => import("@/pages/contact"));
const AiReadiness = lazy(() => import("@/pages/ai-readiness"));
const AiAdoptionAssessment = lazy(() => import("@/pages/ai-adoption-assessment"));
const AiAdoptionReport = lazy(() => import("@/pages/ai-adoption-report"));
const PartnerReport = lazy(() => import("@/pages/partner-report"));
const CoworkRecipeGenerator = lazy(() => import("@/pages/cowork-recipe-generator"));
const CoworkRecipeResults = lazy(() => import("@/pages/cowork-recipe-results"));
const TermsOfService = lazy(() => import("@/pages/terms"));
const KidsProgramTerms = lazy(() => import("@/pages/kids-program-terms"));
const KidsCamperIntake = lazy(() => import("@/pages/kids/camper-intake"));
const KidsDemoNightRsvp = lazy(() => import("@/pages/kids/demo-night-rsvp"));
const KidsShowcasePermission = lazy(() => import("@/pages/kids/showcase-permission"));
const KidsReview = lazy(() => import("@/pages/kids/review"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy"));
const Events = lazy(() => import("@/pages/events"));
const TeamTraining = lazy(() => import("@/pages/team-training"));
const TeamTrainingThankYou = lazy(() => import("@/pages/team-training-thank-you"));
// EventDetail is now eagerly imported above — kept here as a comment marker.
const CheckoutSuccess = lazy(() => import("@/pages/checkout-success"));
const Footer = lazy(() => import("@/components/Footer"));

// ─── Service pages (root-level Webflow slugs) ──────────────────────────────
const Services = lazy(() => import("@/pages/services/index"));
const GoogleAdsManagement = lazy(() => import("@/pages/google-ads-management"));
const MetaAdsManagement = lazy(() => import("@/pages/meta-ads-management"));
const LandingPageAbTesting = lazy(() => import("@/pages/landing-page-ab-testing"));
const GoogleShoppingManagement = lazy(() => import("@/pages/google-shopping-management"));
const DisplayAds = lazy(() => import("@/pages/display-ads"));
const Retargeting = lazy(() => import("@/pages/retargeting"));
const CreativeServices = lazy(() => import("@/pages/creative-services"));
const SeoAgencyServices = lazy(() => import("@/pages/seo-agency-services"));
const AeoServices = lazy(() => import("@/pages/answer-engine-optimization-aeo-services"));
const LinkedinAdsAgency = lazy(() => import("@/pages/linkedin-ads-agency"));
const PinterestAdsAgency = lazy(() => import("@/pages/pinterest-ads-agency"));
const ProgrammaticAdvertising = lazy(() => import("@/pages/programmatic-advertising-agency"));
const GoogleLocalServiceAds = lazy(() => import("@/pages/google-local-service-ads"));
const PayPerClickAgency = lazy(() => import("@/pages/pay-per-click-agency"));
const DigitalAdConsulting = lazy(() => import("@/pages/digital-advertising-consulting"));
const LeadGenServices = lazy(() => import("@/pages/lead-gen-services"));
const AiTransformationServices = lazy(() => import("@/pages/ai-transformation-services"));

// ─── Industry pages ─────────────────────────────────────────────────────────
const HomeServiceLeadGen = lazy(() => import("@/pages/home-service-lead-generation"));
const SaasIndustry = lazy(() => import("@/pages/industries/saas"));
const AutomotiveIndustry = lazy(() => import("@/pages/industries/automotive"));
const FitnessIndustry = lazy(() => import("@/pages/industries/fitness-sporting-goods"));

// ─── Consulting pages ────────────────────────────────────────────────────────
const ConsultingAudit = lazy(() => import("@/pages/consulting/audit"));
const ConsultingAi = lazy(() => import("@/pages/consulting/ai"));

// ─── Training pages ──────────────────────────────────────────────────────────
const Dolah = lazy(() => import("@/pages/training/dolah"));
const ClaudeCodeWorkshop = lazy(() => import("@/pages/training/claude-code-workshop"));
const ClaudeCodeWorkshopV2 = lazy(() => import("@/pages/training/claude-code-workshop-v2"));
const ClaudeDesignBriefToCreative = lazy(() => import("@/pages/training/claude-design-brief-to-creative"));
const ClaudeDesignBriefToCreativeKit = lazy(() => import("@/pages/training/claude-design-brief-to-creative-kit"));
const ClaudeForDesignWorkshopSlides = lazy(() => import("@/pages/training/claude-for-design-workshop-slides"));
const ClaudeForDesignWorkshopKit = lazy(() => import("@/pages/training/claude-for-design-workshop-kit"));
const ClaudeCoworkWorkshop = lazy(() => import("@/pages/training/claude-cowork-workshop"));
const ClaudeCoworkWorkshopPrint = lazy(() => import("@/pages/training/claude-cowork-workshop-print"));
const ClaimfoxCoworkWorkshop = lazy(() => import("@/pages/training/claimfox-cowork-workshop"));
const ClaudeForSalesWorkshop = lazy(() => import("@/pages/training/claude-for-sales-workshop"));
const MediscaTrainingWorkshop = lazy(() => import("@/pages/training/medisca-training-workshop"));
const WealthmastersCoworkWorkshop = lazy(() => import("@/pages/training/wealthmasters-cowork-workshop"));
const MobilefuseSalesWorkshop = lazy(() => import("@/pages/training/mobilefuse-sales-workshop"));
const ClaudeDatavizWorkshop = lazy(() => import("@/pages/training/claude-dataviz-workshop"));
const KidsOrientation = lazy(() => import("@/pages/training/kids-orientation"));
const KidsSession1 = lazy(() => import("@/pages/training/kids-session1"));
const KidsTonight = lazy(() => import("@/pages/kids/kids-tonight"));

// ─── Other new pages ─────────────────────────────────────────────────────────
const Sherpa = lazy(() => import("@/pages/sherpa"));
const Clients = lazy(() => import("@/pages/clients"));
const Ecommerce = lazy(() => import("@/pages/ecommerce"));
// LeadGeneration deleted — redirected to /lead-gen-services
const Books = lazy(() => import("@/pages/books"));
const ReadingList = lazy(() => import("@/pages/reading-list"));
const Tools = lazy(() => import("@/pages/tools"));
const Careers = lazy(() => import("@/pages/careers"));

// Hidden lab/dev route — design prototype, noindexed, not in any nav.
// /lab/museum: the three.js Impressionist Room test page (2026-09-09).
// The page is an SSR-safe shell; the three.js scene is a second lazy chunk
// it mounts after hydration, so three never enters the entry bundle.
const LabMuseum = lazy(() => import("@/pages/lab/museum"));

// ─── Admin pages ─────────────────────────────────────────────────────────────
const AdminDashboardPage = lazy(() => import("@/pages/admin/admin-dashboard"));
const AdminCalendarPage = lazy(() => import("@/pages/admin/admin-calendar"));
const AdminEventsPage = lazy(() => import("@/pages/admin/admin-events"));
const AdminEventDetailPage = lazy(() => import("@/pages/admin/admin-event-detail"));
const AdminOrdersPage = lazy(() => import("@/pages/admin/admin-orders"));
const AdminCaseStudiesPage = lazy(() => import("@/pages/admin/admin-case-studies"));
const AdminClientsPage = lazy(() => import("@/pages/admin/admin-clients"));
const AdminTestimonialsPage = lazy(() => import("@/pages/admin/admin-testimonials"));
const AdminBlogPage = lazy(() => import("@/pages/admin/admin-blog"));
const AdminEmailsPage = lazy(() => import("@/pages/admin/admin-emails"));
const AdminTeamPage = lazy(() => import("@/pages/admin/admin-team"));
const AdminWorkshopSeriesPage = lazy(() => import("@/pages/admin/admin-workshop-series"));
const AdminCouponsPage = lazy(() => import("@/pages/admin/admin-coupons"));
const AdminProPage = lazy(() => import("@/pages/admin/admin-pro"));
const AdminAssetsPage = lazy(() => import("@/pages/admin/admin-assets"));
const AdminStudioPage = lazy(() => import("@/pages/admin/admin-studio"));
const AdminRelationshipsPage = lazy(() => import("@/pages/admin/admin-relationships"));
const AdminShowcasePage = lazy(() => import("@/pages/admin/admin-showcase"));
const AdminLeadsPage = lazy(() => import("@/pages/admin/admin-leads"));
const AdminAdoptionPage = lazy(() => import("@/pages/admin/admin-adoption"));
const ReminderConfirmedPage = lazy(() => import("@/pages/reminder-confirmed"));
import { AdminLayout } from "@/pages/admin/admin-layout";

function PageLoader() {
  return <AppLoading />;
}

function AdminRouter() {
  return (
    <AdminLayout>
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/admin" component={AdminDashboardPage} />
          <Route path="/admin/calendar" component={AdminCalendarPage} />
          <Route path="/admin/events/:id" component={AdminEventDetailPage} />
          <Route path="/admin/events" component={AdminEventsPage} />
          <Route path="/admin/orders" component={AdminOrdersPage} />
          <Route path="/admin/leads" component={AdminLeadsPage} />
          <Route path="/admin/adoption" component={AdminAdoptionPage} />
          <Route path="/admin/coupons" component={AdminCouponsPage} />
          <Route path="/admin/pro" component={AdminProPage} />
          <Route path="/admin/emails" component={AdminEmailsPage} />
          <Route path="/admin/case-studies" component={AdminCaseStudiesPage} />
          <Route path="/admin/clients" component={AdminClientsPage} />
          <Route path="/admin/testimonials" component={AdminTestimonialsPage} />
          <Route path="/admin/relationships" component={AdminRelationshipsPage} />
          <Route path="/admin/showcase" component={AdminShowcasePage} />
          <Route path="/admin/blog" component={AdminBlogPage} />
          <Route path="/admin/team" component={AdminTeamPage} />
          <Route path="/admin/workshop-series" component={AdminWorkshopSeriesPage} />
          <Route path="/admin/assets" component={AdminAssetsPage} />
          <Route path="/admin/studio" component={AdminStudioPage} />
          <Route>{() => <AdminDashboardPage />}</Route>
        </Switch>
      </Suspense>
    </AdminLayout>
  );
}

function SiteRoutes() {
  return (
    <Switch>
      {/* Membership surface */}
      <Route path="/login" component={MemberLogin} />
      <Route path="/signup" component={MemberSignup} />
      <Route path="/claim" component={MemberClaim} />
      <Route path="/auth/verify" component={MemberAuthVerify} />
      <Route path="/auth/magic" component={MemberAuthMagic} />
      <Route path="/auth/reset" component={MemberAuthReset} />
      <Route path="/dashboard/slides/:eventSlug" component={MemberSlides} />
      <Route path="/dashboard" component={MemberDashboard} />
      {/* Claude Design Builder: member program, steps in shared/design-builder.ts */}
      <Route path="/design-builder/:step" component={DesignBuilder} />
      <Route path="/design-builder" component={DesignBuilder} />
      {/* Dev only: the Tutorials pane without a session, so it can be checked in
          a client-only build. Vite drops this branch from production. */}
      {import.meta.env.DEV && <Route path="/_dev/tutorials" component={DevTutorials} />}
      {PRO_LIVE && <Route path="/membership">{() => <MembershipPage />}</Route>}
      {PRO_LIVE && <Route path="/promo">{() => <MembershipPage promo />}</Route>}
      {PRO_LIVE && <Route path="/membership/success" component={MembershipSuccess} />}
      {PRO_LIVE && <Route path="/membership/first-month" component={MembershipFirstMonth} />}
      <Route path="/ai-context-toolkit" component={AiContextToolkit} />
      <Route path="/kit" component={KitHome} />
      <Route path="/kit/build" component={KitBuild} />
      <Route path="/kit/improve" component={KitImprove} />
      <Route path="/kit/audit" component={KitAudit} />
      <Route path="/kit/method" component={KitMethod} />

      {/* Core pages */}
      <Route path="/" component={Home} />
      <Route path="/about-us" component={AboutUs} />
      <Route path="/instructors" component={InstructorsIndex} />
      <Route path="/instructors/:slug" component={InstructorDetail} />
      <Route path="/vip-session" component={VipSession} />
      <Route path="/nyf" component={NyfWorkshop} />
      <Route path="/contact" component={Contact} />
      <Route path="/clients" component={Clients} />
      <Route path="/careers" component={Careers} />

      {/* Services hub */}
      <Route path="/services" component={Services} />

      {/* Service pages (root-level Webflow slugs) */}
      <Route path="/google-ads-management" component={GoogleAdsManagement} />
      <Route path="/meta-ads-management" component={MetaAdsManagement} />
      <Route path="/landing-page-ab-testing" component={LandingPageAbTesting} />
      <Route path="/google-shopping-management" component={GoogleShoppingManagement} />
      <Route path="/display-ads" component={DisplayAds} />
      <Route path="/retargeting" component={Retargeting} />
      <Route path="/creative-services" component={CreativeServices} />
      <Route path="/seo-agency-services" component={SeoAgencyServices} />
      <Route path="/answer-engine-optimization-aeo-services" component={AeoServices} />
      <Route path="/linkedin-ads-agency" component={LinkedinAdsAgency} />
      <Route path="/pinterest-ads-agency" component={PinterestAdsAgency} />
      <Route path="/programmatic-advertising-agency" component={ProgrammaticAdvertising} />
      <Route path="/google-local-service-ads" component={GoogleLocalServiceAds} />
      <Route path="/pay-per-click-agency" component={PayPerClickAgency} />
      <Route path="/digital-advertising-consulting" component={DigitalAdConsulting} />
      <Route path="/lead-gen-services" component={LeadGenServices} />
      <Route path="/ai-transformation-services" component={AiTransformationServices} />

      {/* Consulting */}
      <Route path="/consulting/audit" component={ConsultingAudit} />
      <Route path="/consulting/ai" component={ConsultingAi} />

      {/* Training */}
      <Route path="/training/dolah" component={Dolah} />
      <Route path="/training/claude-code-workshop-slides-v2" component={ClaudeCodeWorkshopV2} />
      {/* Kit before the deck so the deck route never shadows it */}
      <Route path="/training/claude-design-brief-to-creative/kit" component={ClaudeDesignBriefToCreativeKit} />
      <Route path="/training/claude-design-brief-to-creative" component={ClaudeDesignBriefToCreative} />
      <Route path="/training/claude-for-design-workshop-slides" component={ClaudeForDesignWorkshopSlides} />
      <Route path="/training/claude-for-design-workshop-kit" component={ClaudeForDesignWorkshopKit} />
      <Route path="/training/claude-code-workshop-slides" component={ClaudeCodeWorkshop} />
      {/* Print route must come before the deck route so Wouter doesn't match the prefix */}
      <Route path="/training/claude-cowork-workshop-slides/print" component={ClaudeCoworkWorkshopPrint} />
      <Route path="/training/claude-cowork-workshop-slides" component={ClaudeCoworkWorkshop} />
      <Route path="/training/claimfox-cowork-workshop-slides" component={ClaimfoxCoworkWorkshop} />
      <Route path="/training/claude-for-sales-workshop-slides" component={ClaudeForSalesWorkshop} />
      <Route path="/training/medisca-training-slides" component={MediscaTrainingWorkshop} />
      <Route path="/training/wealthmasters-cowork-slides" component={WealthmastersCoworkWorkshop} />
      <Route path="/training/mobilefuse-sales-slides" component={MobilefuseSalesWorkshop} />
      <Route path="/training/claude-dataviz-workshop-slides" component={ClaudeDatavizWorkshop} />
      <Route path="/training/kids-parent-orientation-slides" component={KidsOrientation} />
      <Route path="/training/kids-camp/session-1" component={KidsSession1} />
      <Route path="/kids/tonight" component={KidsTonight} />

      {/* Platform */}
      <Route path="/sherpa" component={Sherpa} />

      {/* Industry */}
      <Route path="/ecommerce" component={Ecommerce} />
      <Route path="/lead-generation">{() => { window.location.replace("/lead-gen-services"); return null; }}</Route>
      <Route path="/home-service-lead-generation" component={HomeServiceLeadGen} />
      <Route path="/industries/saas" component={SaasIndustry} />
      <Route path="/industries/automotive" component={AutomotiveIndustry} />
      <Route path="/industries/fitness-sporting-goods" component={FitnessIndustry} />

      {/* Content & Resources */}
      <Route path="/blog" component={Blog} />
      <Route path="/blog/:slug" component={BlogPost} />
      <Route path="/case-studies" component={CaseStudies} />
      <Route path="/case-studies/:slug" component={CaseStudy} />
      <Route path="/reminder/confirmed" component={ReminderConfirmedPage} />
      {/* /workshops is the canonical URL going forward. Cloudflare 301s
          /events and /events/* to /workshops/* at the edge; we keep
          /events routes registered as a safety net (catches direct hits
          during propagation or if the CF rule is ever disabled). */}
      <Route path="/workshops/:slug/success" component={CheckoutSuccess} />
      <Route path="/workshops/:slug" component={EventDetail} />
      <Route path="/workshops" component={Events} />
      <Route path="/events/:slug/success" component={CheckoutSuccess} />
      <Route path="/events/:slug" component={EventDetail} />
      <Route path="/events" component={Events} />
      <Route path="/ai-training-for-teams/thank-you" component={TeamTrainingThankYou} />
      <Route path="/ai-training-for-teams" component={TeamTraining} />
      <Route path="/team-training">
        {() => { window.location.replace("/ai-training-for-teams"); return null; }}
      </Route>
      <Route path="/books" component={Books} />
      <Route path="/reading-list" component={ReadingList} />
      <Route path="/tools" component={Tools} />

      {/* Utility */}
      <Route path="/ai-readiness" component={AiReadiness} />
      {/* Report route first: wouter matches in order, and the token path is more specific. */}
      <Route path="/ai-adoption-assessment/report/:token" component={AiAdoptionReport} />
      <Route path="/partner/:token" component={PartnerReport} />
      <Route path="/ai-adoption-assessment" component={AiAdoptionAssessment} />
      <Route path="/claude-cowork-recipe-generator/r/:token" component={CoworkRecipeResults} />
      <Route path="/claude-cowork-recipe-generator" component={CoworkRecipeGenerator} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/claude-code-for-kids/program-terms" component={KidsProgramTerms} />
      <Route path="/claude-code-for-kids/build-profile" component={KidsCamperIntake} />
      <Route path="/claude-code-for-kids/demo-night" component={KidsDemoNightRsvp} />
      <Route path="/claude-code-for-kids/showcase" component={KidsShowcasePermission} />
      <Route path="/claude-code-for-kids/review" component={KidsReview} />

      {/* Standalone content — accidentally removed during the /events →
          /workshops rename (commit 10ddb88) along with team-training and
          checkout, both of which were already restored. PAGE_META entries
          in seo.tsx + server/page-meta.ts still exist for these routes. */}
      <Route path="/testimonials" component={Testimonials} />
      <Route path="/measurement" component={Measurement} />
      <Route path="/taylor-swift-effect-impact-on-nfl-super-bowl-lviii" component={TaylorSwiftEffect} />

      {/* Internal */}
      <Route path="/design-system/symbols" component={DesignSystemSymbols} />
      <Route path="/design-system" component={DesignSystem} />
      <Route path="/nav-wireframe" component={NavWireframe} />
      <Route path="/lab/museum" component={LabMuseum} />

      <Route component={NotFound} />
    </Switch>
  );
}

function SiteRouter() {
  const [location] = useLocation();
  const isAdminRoute = location.startsWith("/admin");
  const isDesignSystem = location === "/design-system";
  // True only when the document carries no SSR-emitted JSON-LD (pure-SPA
  // fallback, e.g. dev client-only mode). Lazy init — checked once.
  const [needsFallbackJsonLd] = useState(
    () =>
      typeof document === "undefined" ||
      document.querySelector('script[type="application/ld+json"][data-ssr-jsonld]') === null,
  );
  useEffect(() => {
    rememberLandingAttribution();
  }, []);
  // The cowork + data-viz workshop decks are section-per-view slide kits — the
  // global site footer would render at the bottom of every section, so it's excluded.
  const isSlideDeck =
    // The Mirren AI Forum talk and its kit: presented live, and the kit ends
    // on its own booking card, so no site footer and no promo popup.
    location.startsWith("/training/claude-design-brief-to-creative") ||
    location.startsWith("/training/claude-for-design-workshop-slides") ||
    location.startsWith("/training/claude-for-design-workshop-kit") ||
    location.startsWith("/training/claude-cowork-workshop-slides") ||
    location.startsWith("/training/claimfox-cowork-workshop-slides") ||
    location.startsWith("/training/claude-for-sales-workshop-slides") ||
    location.startsWith("/training/medisca-training-slides") ||
    location.startsWith("/training/wealthmasters-cowork-slides") ||
    location.startsWith("/training/mobilefuse-sales-slides") ||
    location.startsWith("/training/claude-dataviz-workshop-slides") ||
    // Kids parent orientation is presented live to 35 families — a promo
    // popup over the slides mid-session would be worse than the footer.
    location.startsWith("/training/kids-parent-orientation-slides") ||
    // Session decks are on 40 campers' screens live. Same reasoning.
    location.startsWith("/training/kids-camp/") ||
    // The camper page 40 kids open mid-session on their own machines. A promo
    // takeover 25 seconds in, over the copy button, is the worst possible bug.
    location.startsWith("/kids/");
  // Site-wide promo popup is excluded on workshop detail pages (which have
  // their own email-opt-in popup) and the post-checkout success page. The
  // regex matches `/workshops/<anything>` so both `/workshops/:slug` and
  // `/workshops/:slug/success` are excluded; `/workshops` (overview) is not.
  // Both /workshops/:slug and /events/:slug route to EventDetail, which mounts
  // its own ReminderPopup. Match both so the GlobalPromoPopup doesn't stack a
  // second exit-intent popup on top of it.
  const isWorkshopDetailRoute =
    /^\/(workshops|events)\/.+/.test(location) ||
    // Enrolled-families-only pages. These parents have already bought; a promo
    // popup over a form they were asked to fill out is pure friction.
    location.startsWith("/claude-code-for-kids/");
  // Membership surface: never show the promo popup to someone signing in,
  // using their dashboard, or reading the membership page (Patrick, Oct 1:
  // the workshop pitch popping over the membership pitch, mid-scroll on a
  // phone, is the wrong ad in the wrong room).
  // Lab pages are drag-to-look canvases; an exit-intent popup firing when the
  // pointer leaves the top of the stage would land mid-gesture.
  const isLabRoute = location.startsWith("/lab/");
  // The adoption assessment is administered by a client to their own staff, and
  // the leadership report is a paid deliverable. A promo popup pitching public
  // workshops over either one reads as an ad inside their internal process.
  const isAdoptionRoute = location.startsWith("/ai-adoption-assessment");
  // A paid booking flow: no exit-intent promo for other workshops mid-checkout.
  const isVipRoute = location.startsWith("/vip-session") || location === "/nyf";
  // The toolkit landing page has one ask; no workshop promo popup on top of it.
  const isToolkitLanding = location === "/ai-context-toolkit";
  // Reading pages get the free toolkit popup instead of the workshop one:
  // someone reading a post is earlier than someone choosing a workshop.
  const isToolkitPopupRoute =
    location === "/blog" ||
    location.startsWith("/blog/") ||
    location.startsWith("/case-studies") ||
    location === "/tools";
  const isMemberRoute =
    location.startsWith("/dashboard") ||
    location === "/design-builder" ||
    location.startsWith("/design-builder/") ||
    location.startsWith("/auth/") ||
    location === "/login" ||
    location === "/signup" ||
    location === "/claim" ||
    location.startsWith("/membership") ||
    location === "/promo";

  // Disable the browser's auto scroll restoration once per session — we control
  // it ourselves below. Without this, Chrome/Safari will sometimes restore the
  // previous page's scrollY after a wouter pushState, defeating the scroll.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Scroll to top on route change (unless navigating to a hash anchor).
  //
  // Why the double scroll: the routed tree is wrapped in <Suspense> with a
  // min-h-screen <PageLoader> fallback. When the destination chunk is COLD,
  // the fallback renders first, the body height collapses to one viewport,
  // and scrollY clamps to 0 naturally. When the chunk is WARM (lazy already
  // resolved — e.g., revisiting the events page), Suspense does NOT fall
  // back, the body never shrinks, and the browser preserves the prior
  // scrollY across the route swap. The single scrollTo(0,0) inside the
  // effect fires AFTER commit, so the user briefly sees the new page at the
  // old scrollY before it jumps — and on some browsers it doesn't jump at
  // all because the effect ran while a layout pass was still pending. The
  // requestAnimationFrame retry guarantees the scroll lands after the new
  // page's first paint, regardless of warm/cold suspense state.
  // Scroll-to-top on route change.
  //
  // The previous version called window.scrollTo(0,0) which DID NOT WORK
  // because Lenis (the smooth-scroll library) runs a global requestAnimationFrame
  // loop that writes its own cached scroll position back to window.scrollY on
  // every frame. So our scrollTo(0,0) succeeded for one frame and Lenis
  // immediately undid it, snapping the user back to the previous page's
  // scroll position. Hence the long-standing "lands mid-page after clicking
  // a link" bug.
  //
  // The fix: ask Lenis directly to scroll to 0 with `immediate: true` (no
  // animation) and `force: true` (override its cached target). For non-touch
  // desktop sessions where Lenis is active, this resets the internal target.
  // For mobile/touch sessions where Lenis isn't mounted, we fall back to
  // window.scrollTo(0,0) which works correctly there.
  //
  // The setTimeout(120ms) covers the cold-chunk Suspense case: when a
  // lazy-loaded route resolves after the location change, the destination
  // mounts a tall body — by then our initial reset has already fired, so we
  // re-fire after a short delay to catch the new layout.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) return;

    const reset = () => {
      const lenis = getLenis();
      if (lenis) {
        lenis.scrollTo(0, { immediate: true, force: true });
      } else {
        window.scrollTo(0, 0);
      }
    };

    reset();
    const raf = requestAnimationFrame(reset);
    const timeout = window.setTimeout(reset, 120);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [location]);

  if (isAdminRoute) {
    return <AdminRouter />;
  }

  return (
    <>
      {/* SSR already injects WebSite (on /) and Organization (on /about-us,
          /clients) JSON-LD into the head, tagged data-ssr-jsonld. Only emit
          the client-side copies when SSR didn't run — otherwise the rendered
          DOM carries duplicate entities with drifting fields (GSC dup-entity
          warnings). needsFallbackJsonLd is computed once at mount. */}
      {needsFallbackJsonLd && (
        <>
          <JsonLd data={organizationSchema()} />
          <JsonLd data={websiteSchema()} />
        </>
      )}
      <Suspense fallback={<PageLoader />}>
        <SiteRoutes />
        {/* Account screens (/login, /signup, /claim, /auth/*) are a full-screen split
            with their own Terms · Privacy line.
            The member dashboard runs without the footer for now (Esther, 2026-10-29),
            and so does the AI Context Toolkit, which runs full screen. */}
        {!isDesignSystem && !isSlideDeck && !isAuthScreenPath(location) && !isToolkitPath(location) && !location.startsWith("/dashboard") && (
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        )}
      </Suspense>
      {!isWorkshopDetailRoute && !isDesignSystem && !isSlideDeck && !isMemberRoute && !isLabRoute && !isAdoptionRoute && !isVipRoute && !isToolkitLanding && !isToolkitPopupRoute && (
        <Suspense fallback={null}>
          <GlobalPromoPopup />
        </Suspense>
      )}
      {isToolkitPopupRoute && (
        <Suspense fallback={null}>
          <ToolkitPopup />
        </Suspense>
      )}
    </>
  );
}

export default function App({ queryClientOverride }: { queryClientOverride?: QueryClient }) {
  // When queryClientOverride is provided, entry-client already wraps with
  // QueryClientProvider + HydrationBoundary — don't nest another one.
  const content = (
    <TooltipProvider>
      <SiteRouter />
      <ClientOnly>
        <SmoothScrollProvider>
          <Suspense fallback={null}>
            <Toaster />
          </Suspense>
          <Suspense fallback={null}>
            <SonnerToaster
              position="top-right"
              toastOptions={{
                style: {
                  fontFamily: "var(--font-sans)",
                  borderRadius: 0,
                  border: "1.5px solid var(--input-border)",
                  boxShadow: "none",
                },
              }}
            />
          </Suspense>
          <BackToTop />
        </SmoothScrollProvider>
      </ClientOnly>
    </TooltipProvider>
  );

  if (queryClientOverride) return content;

  return (
    <QueryClientProvider client={defaultQueryClient}>
      {content}
    </QueryClientProvider>
  );
}

/** Renders children only after hydration — avoids SSR mismatch for browser-only components */
function ClientOnly({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? <>{children}</> : null;
}
